import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor} from 'react-intl';
import {DataGranularity} from '../../../api/types';
import * as Units from '../../Units';
import {DateTimeConfig} from '../../Dates';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint, SeriesConfig} from '../types';

export interface PressureChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  granularity: DataGranularity;
  height?: number;
  comparisonData?: ChartDataPoint[];
  comparisonLabel?: string;
  dateTimeConfig?: DateTimeConfig;
  /** When provided, uses category x-axis instead of datetime (for analysis charts) */
  categories?: string[];
  /**
   * Names the series in the user's language. Required: the names are
   * messages, and English would otherwise reach every locale.
   */
  formatMessage: (descriptor: MessageDescriptor) => string;
}

export const pressureChart = (options: PressureChartOptions): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    granularity,
    height = 250,
    comparisonData,
    comparisonLabel,
    dateTimeConfig,
    categories,
    formatMessage,
  } = options;

  const hasRange = ['D', 'W', 'M'].includes(granularity);

  const series: SeriesConfig[] = [
    {
      id: 'pressure',
      type: 'line',
      dataKey: 'SLP',
      name: formatMessage(messages.metrics.pressure),
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      lineWidth: 2,
      showMarkers: false,
      zIndex: 2,
    },
  ];

  if (hasRange) {
    series.push({
      id: 'pressure-range',
      type: 'areaRange',
      dataKey: 'SLP_RANGE',
      rangeKey: 'SLP_RANGE',
      name: formatMessage(messages.charts.minMaxRange),
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      fillOpacity: 0.2,
      lineWidth: 0,
      showMarkers: false,
      zIndex: 0,
    });
  }

  if (comparisonData) {
    series.push({
      id: 'pressure-comparison',
      type: 'line',
      dataKey: 'SLP_COMP',
      name: comparisonLabel ?? formatMessage(messages.charts.comparison),
      color: theme.colors.textSecondary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      lineWidth: 2,
      dashArray: '5 3',
      showMarkers: false,
      zIndex: 1,
    });
  }

  return {
    height,
    data,
    series,
    xAxis: createXAxisConfig({
      type: categories ? 'category' : 'datetime',
      timezone,
      granularity,
      theme,
      categories,
    }),
    yAxes: [
      createYAxisConfig({
        id: 'pressure',
        metric: 'SLP',
        units,
        theme,
      }),
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity,
      metric: 'SLP',
      units,
      theme,
      dateTimeConfig,
      tooltipLabels: categories,
    }),
    legend: createLegendConfig(series.length > 2, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
